/**
 * services/TradeExecutionService.ts
 * Service for TradeExecution — the event-projection model for a live or closed position.
 *
 * TradeExecution is NOT the canonical record. TradeExecutionEvent is canonical.
 * This service owns two responsibilities:
 *
 *   1. createForPromotion() — creates the initial pending TradeExecution record when
 *      a draft is promoted. Always called inside a caller-supplied Dexie transaction.
 *
 *   2. updateProjectionFromEvent() — recomputes projection fields from the full event
 *      sequence and writes the diff. Always called inside a caller-supplied Dexie
 *      transaction, after appendEvent() has already written the triggering event.
 *
 * §8.4 Atomic write contract — the caller (TradePromotionService / future action handlers)
 * is responsible for the full three-step sequence inside a single transaction:
 *   1. TradeExecutionEventService.appendEvent()
 *   2. updateProjectionFromEvent()   ← this service (step 2)
 *   3. BalanceHistoryService.addExecutionTransaction() — for financial-impact events
 *
 * Projection recompute approach:
 * All VWAP and status fields are derived by iterating the full event sequence, not
 * by incremental update. This avoids accumulated floating-point drift and is correct
 * for any order/combination of events. The overhead is acceptable for WU2B event counts.
 *
 * @see TradeExecutionEvent — canonical immutable event log
 * @see TradeExecutionEventService — append-only event write service (step 1)
 * @see BalanceHistoryService.addExecutionTransaction — balance row service (step 3)
 */

import { BaseService } from './BaseService';
import { isTombstoned } from '@/utils/tombstone';
import { generateUUID } from '@/utils/uuid';
import { computeActiveOppositeOrderReservation, computeMaxOppositeOrderQty, computeRelationalPriceConstraint, getActiveOppositeOrderPrices } from '@/utils/tradeExecutionCap';
import { db } from '@/lib/db';
import { markAsInstrumentedWriteContext } from './sync/SyncImportContext';
import { getSyncOperationService } from './sync/SyncOperationService';
import { buildSetOps, buildDiffOps } from './sync/OperationBuilder';
import { tradeExecutionEventService } from './TradeExecutionEventService';
import { balanceHistoryService } from './BalanceHistoryService';
import type { TradeExecution, TradeExecutionStatus } from '@/types/schema/TradeExecution';
import type { TradeExecutionEvent } from '@/types/schema/TradeExecutionEvent';
import type { BalanceHistory } from '@/types/schema/BalanceHistory';
import type { InstrumentSnapshot } from '@/types/shared/InstrumentSnapshot';
import type { RiskSnapshot } from '@/types/shared/RiskSnapshots';

// ── Input Types ───────────────────────────────────────────────────────────────

/**
 * Input for logging an entry-fill event on an existing TradeExecution.
 * §8.4: appendEvent → updateProjectionFromEvent → addExecutionTransaction — all atomic.
 */
export interface LogFillInput {
  tradeExecutionId: string;
  tradeId: string;
  sessionId: string;
  /** Caller-supplied idempotency key */
  operationId: string;
  direction: 'long' | 'short';
  quantity: number;
  price: number;
  occurredAt: string;
  /**
   * ID of the originating `order-placed` event, when this fill closes a resting order.
   * Omit for unlinked fills (e.g. "Add to Position" without a prior order row).
   */
  targetOrderEventId?: string;
  /**
   * Optional balance row — omit when P&L is not yet available (manual entry without
   * broker reconciliation). Balance rows will be back-filled by S03 reconciliation.
   */
  balanceRow?: {
    accountId: string;
    amount: number;
    transactionType: BalanceHistory['transactionType'];
    description?: string;
  };
}

/**
 * Per-order cancel allocation for a fill event.
 * Used by `LogCloseInput.cancelAllocations` to cancel or reduce active exit orders
 * that become over-reserved when a partial or final exit is recorded.
 */
export interface FillCancelAllocation {
  /** ID of the `order-placed` event to cancel or reduce. */
  orderEventId: string;
  /**
   * Quantity to cancel from this order's remaining quantity.
   * - Values <= 0 are silently skipped.
   * - Must not exceed the order's remaining quantity (throws if violated).
   * - Equal to order's remaining quantity → `order-cancelled` event.
   * - Less than order's remaining quantity → `order-amended` event with reduced qty.
   */
  cancelQty: number;
}

/**
 * Input for logging a partial-exit or final-exit event on an existing TradeExecution.
 * §8.4: appendEvent → updateProjectionFromEvent → addExecutionTransaction — all atomic.
 */
export interface LogCloseInput {
  tradeExecutionId: string;
  tradeId: string;
  sessionId: string;
  /** Caller-supplied idempotency key */
  operationId: string;
  eventType: 'partial-exit' | 'final-exit';
  quantity: number;
  price: number;
  occurredAt: string;
  /**
   * ID of the originating `order-placed` event, when this exit closes a resting order.
   * Omit for unlinked exits (e.g. "Close" button without a prior order row context).
   * Used by the Execution tab to track per-order fill state for opposite-direction orders.
   */
  targetOrderEventId?: string;
  /**
   * T03 (WU2E): Optional per-order cancel allocations.
   * After writing the fill event, each allocation is processed atomically inside the
   * same transaction: full allocation writes `order-cancelled`, partial writes `order-amended`.
   * Post-fill per-type reservation validation follows (partial-exit) or auto-cancel (final-exit).
   */
  cancelAllocations?: FillCancelAllocation[];
  /**
   * Optional balance row — omit when P&L is not yet available (manual entry without
   * broker reconciliation). Balance rows will be back-filled by S03 reconciliation.
   */
  balanceRow?: {
    accountId: string;
    amount: number;
    transactionType: BalanceHistory['transactionType'];
    description?: string;
  };
}

/**
 * Input for logging a non-financial order event on an existing TradeExecution.
 * §8.4: appendEvent → updateProjectionFromEvent only (no balance row for order events).
 */
export interface LogOrderInput {
  tradeExecutionId: string;
  tradeId: string;
  sessionId: string;
  /** Caller-supplied idempotency key */
  operationId: string;
  occurredAt: string;
  /** Trade direction */
  direction: 'long' | 'short';
  /** Intended quantity for this order */
  quantity: number;
  /** Intended price — required for all order types */
  price: number;
  /** Order type */
  orderType: 'limit' | 'stop';
}

/**
 * Input for logging an order-amended event on an existing TradeExecution.
 * Non-financial event: no balance row required.
 * §8.4 atomic write: appendEvent → updateProjectionFromEvent (steps 1–2 only).
 */
export interface LogAmendInput {
  tradeExecutionId: string;
  tradeId: string;
  sessionId: string;
  /** Caller-supplied idempotency key */
  operationId: string;
  occurredAt: string;
  /** ID of the original order-placed event being amended */
  targetOrderEventId: string;
  /** New quantity — omit if only price is changing */
  quantity?: number;
  /** New price — omit if only quantity is changing */
  price?: number;
}

/**
 * Input for logging an order-cancelled event on an existing TradeExecution.
 * Non-financial event: no balance row required.
 * §8.4 atomic write: appendEvent → updateProjectionFromEvent (steps 1–2 only).
 */
export interface LogCancelOrderInput {
  tradeExecutionId: string;
  tradeId: string;
  sessionId: string;
  /** Caller-supplied idempotency key */
  operationId: string;
  occurredAt: string;
  /** ID of the original order-placed event being cancelled */
  targetOrderEventId: string;
}

/**
 * Input for creating a TradeExecution during draft promotion.
 * All required fields must be resolved before calling createForPromotion().
 */
export interface CreateForPromotionInput {
  /** The Trade this execution implements */
  tradeId: string;
  /** The specific draft instance that was promoted */
  sourceDraftId: string;
  /** Trading account to use for this execution */
  accountId: string;
  /** Session in which the promotion occurred */
  openedSessionId: string;
  /** Instrument snapshot from the instrument picker at promotion time */
  instrumentSnapshot: InstrumentSnapshot;
  /** Account risk snapshot at time of promotion (optional) */
  riskSnapshot?: RiskSnapshot;
}

/**
 * Computed projection changes returned by the private recompute helper.
 * Only fields that have a definite value after event processing are included.
 */
type ProjectionChanges = Partial<TradeExecution>;

// ── Service ───────────────────────────────────────────────────────────────────

/**
 * Service for TradeExecution — event-driven projection model.
 *
 * Inherits get(), getAll(), softDelete() from BaseService for sync/tombstone flows.
 * Does NOT expose a public update() path — all projection writes go through
 * updateProjectionFromEvent() to ensure the §8.4 contract is followed.
 */
export class TradeExecutionService extends BaseService<TradeExecution> {
  constructor() {
    super('trade_executions');
  }

  // ===== PROMOTION WRITE =====

  /**
   * Create the initial TradeExecution record during draft promotion.
   *
   * IMPORTANT: Must be called inside an active Dexie 'rw' transaction that
   * includes db.trade_executions, db.sync_operations, and db.sync_state.
   * Dexie auto-propagates the active transaction.
   *
   * Idempotency: if a non-tombstoned TradeExecution already exists for this
   * tradeId, it is returned without writing a duplicate.
   *
   * Initial state: executionStatus = 'pending' (no entry-fill event yet).
   * All projection fields (avgEntryPrice, remainingQuantity, etc.) are absent
   * until the first entry-fill event is processed by updateProjectionFromEvent().
   *
   * @param data - Promotion input with all required reference + snapshot fields
   * @returns The created TradeExecution, or the existing one if already promoted
   */
  async createForPromotion(data: CreateForPromotionInput): Promise<TradeExecution> {
    // Idempotency: if a TradeExecution already exists for this trade, return it
    const existing = await this.table
      .where('tradeId')
      .equals(data.tradeId)
      .filter(e => !isTombstoned(e))
      .first();
    if (existing) {
      return existing;
    }

    const now = new Date().toISOString();
    const execution: TradeExecution = {
      id: generateUUID(),
      createdAt: now,
      updatedAt: now,
      tradeId: data.tradeId,
      sourceDraftId: data.sourceDraftId,
      accountId: data.accountId,
      openedSessionId: data.openedSessionId,
      instrumentSnapshot: data.instrumentSnapshot,
      reconciliationStatus: 'approved',
      reconciliationSource: 'manual',
      executionStatus: 'pending',
      ...(data.riskSnapshot !== undefined && { riskSnapshot: data.riskSnapshot }),
    };

    markAsInstrumentedWriteContext();
    await this.table.add(execution);

    const ops = buildSetOps(
      'trade_executions',
      execution.id,
      execution as unknown as Record<string, unknown>
    );
    if (ops.length > 0) {
      await getSyncOperationService().logBatch(ops);
    }

    return execution;
  }

  // ===== PROJECTION UPDATE =====

  /**
   * Recompute and persist TradeExecution projection fields from the full event sequence.
   *
   * IMPORTANT: Must be called inside an active Dexie 'rw' transaction that
   * includes db.trade_executions, db.trade_execution_events, db.sync_operations,
   * and db.sync_state. The triggering event must already be written to
   * db.trade_execution_events (i.e., appendEvent() was called first).
   *
   * Projection recompute iterates ALL events for this tradeExecutionId, including
   * the just-appended one. This approach guarantees accurate VWAP for any number
   * of fills and exits and avoids incremental floating-point drift.
   *
   * Only changed fields produce sync diff-ops (buildDiffOps).
   *
   * @param tradeExecutionId - ID of the TradeExecution to update
   * @param _event - The triggering event (used to assert context; projection reads all events)
   */
  async updateProjectionFromEvent(
    tradeExecutionId: string,
    _event: TradeExecutionEvent
  ): Promise<void> {
    const existing = await this.table.get(tradeExecutionId);
    if (!existing) {
      throw new Error(`TradeExecution ${tradeExecutionId} not found`);
    }

    const changes = await this.recomputeProjection(tradeExecutionId);
    if (Object.keys(changes).length === 0) {
      return;
    }

    // T02 (WU2E): SL/TP are now derived from active exit orders (Decision 4).
    // Dexie 4.x treats `undefined` values in update() as explicit property deletions.
    // Always include these keys so stale legacy values (from stop-moved / initialStopLoss)
    // are cleared when the new derivation yields undefined.
    const updatedData: Record<string, unknown> = {
      ...changes,
      updatedAt: new Date().toISOString(),
      stopLossCurrent: changes.stopLossCurrent,     // undefined → Dexie 4 deletes the property
      takeProfitCurrent: changes.takeProfitCurrent, // undefined → Dexie 4 deletes the property
    };

    markAsInstrumentedWriteContext();
    await this.table.update(
      tradeExecutionId,
      updatedData as unknown as Parameters<typeof this.table.update>[1]
    );

    // Log only changed fields for sync (diff-based, excludes updatedAt)
    const ops = buildDiffOps(
      'trade_executions',
      tradeExecutionId,
      existing as unknown as Record<string, unknown>,
      changes as unknown as Record<string, unknown>
    );
    if (ops.length > 0) {
      await getSyncOperationService().logBatch(ops);
    }
  }

  // ===== QUERIES =====

  /**
   * Get all non-tombstoned TradeExecutions for a Trade.
   *
   * Uses the tradeId index. In WU2B there is one TradeExecution per Trade;
   * returns an array for forward-compatibility with multi-account scenarios.
   *
   * @param tradeId - ID of the parent Trade
   * @returns Array of active TradeExecution records
   */
  async getByTradeId(tradeId: string): Promise<TradeExecution[]> {
    try {
      const records = await this.table
        .where('tradeId')
        .equals(tradeId)
        .toArray();
      return records.filter(r => !isTombstoned(r));
    } catch (error) {
      console.error(`Failed to get trade executions for trade ${tradeId}:`, error);
      throw new Error('Failed to retrieve trade executions');
    }
  }

  /**
   * Get a TradeExecution by ID.
   *
   * Returns undefined if not found or tombstoned.
   * Delegates to BaseService.get() — returns tombstones too; filter in caller if needed.
   *
   * @param id - TradeExecution ID
   * @returns The record or undefined
   */
  async getById(id: string): Promise<TradeExecution | undefined> {
    return this.get(id);
  }

  // ===== POST-PROMOTION WRITES =====

  /**
   * Log an entry-fill event on an existing TradeExecution.
   *
   * Executes the §8.4 atomic write contract inside a single Dexie transaction:
   *   1. TradeExecutionEventService.appendEvent() — entry-fill
   *   2. updateProjectionFromEvent()
   *   3. BalanceHistoryService.addExecutionTransaction() — required for entry-fill
   *
   * Fix #2 (PR61): idempotency guard — if the operationId was already processed,
   * appendEvent() returns the existing event without a duplicate write. The balance
   * row check ensures addExecutionTransaction() is skipped on replay.
   *
   * @returns The updated TradeExecution after projection recompute
   */
  async logFill(input: LogFillInput): Promise<TradeExecution> {
    let result: TradeExecution | undefined;

    await db.transaction('rw', [
      db.trade_execution_events,
      db.trade_executions,
      db.balance_histories,
      db.sync_operations,
      db.sync_state,
    ], async () => {
      markAsInstrumentedWriteContext();

      // F-01 (PR62): guard against fills on terminal executions (mirrors logClose() pattern).
      // Service-layer invariant: the UI gates alone are insufficient per project standards.
      const currentExecution = await this.table.get(input.tradeExecutionId);
      if (!currentExecution) {
        throw new Error(
          `[TradeExecutionService] logFill: TradeExecution ${input.tradeExecutionId} not found`
        );
      }
      if (
        currentExecution.executionStatus === 'closed' ||
        currentExecution.executionStatus === 'cancelled'
      ) {
        throw new Error(
          `Cannot add fills to a ${currentExecution.executionStatus} execution.`
        );
      }

      // Guard against entry-fills whose direction conflicts with the established position.
      // "Add to Position" must be same-direction only; opposite-direction intent uses logClose().
      if (
        currentExecution.positionDirection !== undefined &&
        input.direction !== currentExecution.positionDirection
      ) {
        throw new Error(
          `Cannot add an entry-fill with direction '${input.direction}' — position direction is '${currentExecution.positionDirection}'. Use the Close button or an opposite-direction order fill to reduce the position.`
        );
      }

      const event = await tradeExecutionEventService.appendEvent({
        tradeExecutionId: input.tradeExecutionId,
        tradeId: input.tradeId,
        sessionId: input.sessionId,
        operationId: input.operationId,
        eventType: 'entry-fill',
        occurredAt: input.occurredAt,
        direction: input.direction,
        quantity: input.quantity,
        price: input.price,
        ...(input.targetOrderEventId !== undefined ? { targetOrderEventId: input.targetOrderEventId } : {}),
      });

      await this.updateProjectionFromEvent(input.tradeExecutionId, event);

      // Fix #2 (PR61): idempotency guard — skip balance row if already written for this event.
      // appendEvent() returns the existing event on duplicate operationId; without this guard,
      // addExecutionTransaction() would write a second balance row for the same fill.
      // Balance row is optional — omitted when P&L is not available (manual entry, pre-S03).
      if (input.balanceRow) {
        const existingFillBalance = await db.balance_histories
          .where('sourceExecutionEventId')
          .equals(event.id)
          .first();
        if (!existingFillBalance) {
          await balanceHistoryService.addExecutionTransaction({
            accountId: input.balanceRow.accountId,
            timestamp: input.occurredAt,
            transactionType: input.balanceRow.transactionType,
            amount: input.balanceRow.amount,
            sourceExecutionEventId: event.id,
            tradeExecutionId: input.tradeExecutionId,
            ...(input.balanceRow.description !== undefined ? { description: input.balanceRow.description } : {}),
          });
        }
      }

      // updateProjectionFromEvent returns void — re-read to get updated state
      result = await this.table.get(input.tradeExecutionId);
    });

    if (!result) {
      throw new Error(
        `[TradeExecutionService] logFill: TradeExecution ${input.tradeExecutionId} not found after transaction`
      );
    }

    return result;
  }

  /**
   * Log a partial-exit or final-exit event on an existing TradeExecution.
   *
   * Executes the §8.4 atomic write contract inside a single Dexie transaction:
   *   1. TradeExecutionEventService.appendEvent() — partial-exit or final-exit
   *   2. updateProjectionFromEvent()
   *   3. BalanceHistoryService.addExecutionTransaction() — required for exit events
   *
   * Fix #2 (PR61): idempotency guard — balance row skipped if already written for
   * this operationId's event (prevents duplicate balance rows on double-submit).
   *
   * Fix #4 (PR61): exit preconditions — qty must not exceed remainingQuantity;
   * an entry-fill must have been logged first (remainingQuantity > 0).
   *
   * @returns The updated TradeExecution after projection recompute
   */
  async logClose(input: LogCloseInput): Promise<TradeExecution> {
    let result: TradeExecution | undefined;

    await db.transaction('rw', [
      db.trade_execution_events,
      db.trade_executions,
      db.balance_histories,
      db.sync_operations,
      db.sync_state,
    ], async () => {
      markAsInstrumentedWriteContext();

      // Fix #4 (PR61): exit quantity precondition — qty must not exceed remainingQuantity.
      const currentExecution = await this.table.get(input.tradeExecutionId);
      if (!currentExecution) {
        throw new Error(
          `[TradeExecutionService] logClose: TradeExecution ${input.tradeExecutionId} not found`
        );
      }
      if (
        currentExecution.remainingQuantity === undefined ||
        currentExecution.remainingQuantity <= 0
      ) {
        throw new Error('No open quantity to exit — log an entry-fill first.');
      }
      if (input.quantity > currentExecution.remainingQuantity) {
        throw new Error(
          `Exit quantity ${input.quantity} exceeds remaining quantity ${currentExecution.remainingQuantity}`
        );
      }

      const event = await tradeExecutionEventService.appendEvent({
        tradeExecutionId: input.tradeExecutionId,
        tradeId: input.tradeId,
        sessionId: input.sessionId,
        operationId: input.operationId,
        eventType: input.eventType,
        occurredAt: input.occurredAt,
        quantity: input.quantity,
        price: input.price,
        ...(input.targetOrderEventId !== undefined ? { targetOrderEventId: input.targetOrderEventId } : {}),
      });

      // Update projection after the fill so remainingQuantity reflects the exit before
      // processing allocations. This value is used for post-fill reservation validation.
      await this.updateProjectionFromEvent(input.tradeExecutionId, event);

      // Fix #2 (PR61): idempotency guard — skip balance row if already written for this event.
      // Balance row is optional — omitted when P&L is not available (manual entry, pre-S03).
      if (input.balanceRow) {
        const existingCloseBalance = await db.balance_histories
          .where('sourceExecutionEventId')
          .equals(event.id)
          .first();
        if (!existingCloseBalance) {
          await balanceHistoryService.addExecutionTransaction({
            accountId: input.balanceRow.accountId,
            timestamp: input.occurredAt,
            transactionType: input.balanceRow.transactionType,
            amount: input.balanceRow.amount,
            sourceExecutionEventId: event.id,
            tradeExecutionId: input.tradeExecutionId,
            ...(input.balanceRow.description !== undefined ? { description: input.balanceRow.description } : {}),
          });
        }
      }

      // T03 (WU2E): Process cancel allocations and post-fill invariants.
      // Only runs when a position direction is established (i.e., entry fills exist).
      const positionDirection = currentExecution.positionDirection;
      if (positionDirection !== undefined) {
        // ── Step 1: Process cancel allocations ───────────────────────────────────
        if (input.cancelAllocations && input.cancelAllocations.length > 0) {
          const allocEvents = await db.trade_execution_events
            .where('tradeExecutionId')
            .equals(input.tradeExecutionId)
            .filter(e => !isTombstoned(e))
            .toArray();

          for (const alloc of input.cancelAllocations) {
            if (alloc.cancelQty <= 0) continue;

            const targetOrder = allocEvents.find(
              e => e.eventType === 'order-placed' && e.id === alloc.orderEventId,
            );
            if (!targetOrder) {
              throw new Error(
                `[TradeExecutionService] logClose: allocation target order ${alloc.orderEventId} not found — allocation input is invalid.`,
              );
            }

            // Resolve current quantity (latest qty amend, else original order qty)
            const orderAmends = allocEvents.filter(
              e => e.eventType === 'order-amended' && e.targetOrderEventId === targetOrder.id,
            );
            const qtyAmends = orderAmends.filter(e => e.quantity !== undefined);
            const latestQtyAmend =
              qtyAmends.length > 0
                ? qtyAmends.reduce((a, b) => {
                    if (a.occurredAt !== b.occurredAt) return a.occurredAt > b.occurredAt ? a : b;
                    return a.createdAt > b.createdAt ? a : b;
                  })
                : undefined;
            const currentOrderQty = latestQtyAmend?.quantity ?? targetOrder.quantity;
            if (currentOrderQty === undefined) continue;

            // Compute filled quantity for this order
            const filledQty = allocEvents
              .filter(
                e =>
                  (e.eventType === 'entry-fill' ||
                    e.eventType === 'partial-exit' ||
                    e.eventType === 'final-exit') &&
                  e.targetOrderEventId === targetOrder.id,
              )
              .reduce((sum, e) => sum + (e.quantity ?? 0), 0);

            const orderRemainingQty = currentOrderQty - filledQty;
            if (orderRemainingQty <= 0) continue; // Already fully filled — skip

            if (alloc.cancelQty > orderRemainingQty) {
              throw new Error(
                `Cancel quantity ${alloc.cancelQty} exceeds order remaining quantity ${orderRemainingQty} for order ${alloc.orderEventId}.`,
              );
            }

            // Idempotent operationId: same input → same derived ID → no-op on retry
            const allocOperationId = `${input.operationId}__alloc_${alloc.orderEventId}`;

            if (alloc.cancelQty === orderRemainingQty) {
              // Full cancel
              await tradeExecutionEventService.appendEvent({
                tradeExecutionId: input.tradeExecutionId,
                tradeId: input.tradeId,
                sessionId: input.sessionId,
                operationId: allocOperationId,
                eventType: 'order-cancelled',
                occurredAt: input.occurredAt,
                targetOrderEventId: targetOrder.id,
              });
            } else {
              // Partial reduce: cancelQty ≤ orderRemainingQty (enforced above), so
              // newQty = currentOrderQty - cancelQty ≥ filledQty by construction.
              const newQty = currentOrderQty - alloc.cancelQty;
              await tradeExecutionEventService.appendEvent({
                tradeExecutionId: input.tradeExecutionId,
                tradeId: input.tradeId,
                sessionId: input.sessionId,
                operationId: allocOperationId,
                eventType: 'order-amended',
                occurredAt: input.occurredAt,
                targetOrderEventId: targetOrder.id,
                quantity: newQty,
              });
            }
          }
        }

        // ── Step 2: final-exit → auto-cancel all remaining active exit orders ───
        if (input.eventType === 'final-exit') {
          const freshEvents = await db.trade_execution_events
            .where('tradeExecutionId')
            .equals(input.tradeExecutionId)
            .filter(e => !isTombstoned(e))
            .toArray();

          const cancellations = freshEvents.filter(e => e.eventType === 'order-cancelled');
          const linkedFills = freshEvents.filter(
            e =>
              (e.eventType === 'entry-fill' ||
                e.eventType === 'partial-exit' ||
                e.eventType === 'final-exit') &&
              e.targetOrderEventId !== undefined,
          );

          const activeExitOrders = freshEvents.filter(e => {
            if (e.eventType !== 'order-placed') return false;
            if (e.direction === positionDirection) return false;
            if (e.orderType !== 'stop' && e.orderType !== 'limit') return false;
            if (cancellations.some(c => c.targetOrderEventId === e.id)) return false;
            const amendments = freshEvents.filter(
              a => a.eventType === 'order-amended' && a.targetOrderEventId === e.id,
            );
            const qtyAmends = amendments.filter(a => a.quantity !== undefined);
            const latestQtyAmend =
              qtyAmends.length > 0
                ? qtyAmends.reduce((a, b) => {
                    if (a.occurredAt !== b.occurredAt) return a.occurredAt > b.occurredAt ? a : b;
                    return a.createdAt > b.createdAt ? a : b;
                  })
                : undefined;
            const currentQty = latestQtyAmend?.quantity ?? e.quantity;
            if (currentQty === undefined) return false;
            const filledQty = linkedFills
              .filter(f => f.targetOrderEventId === e.id)
              .reduce((sum, f) => sum + (f.quantity ?? 0), 0);
            return filledQty < currentQty;
          });

          for (const order of activeExitOrders) {
            const autoCancelOpId = `${input.operationId}__autocancel_${order.id}`;
            await tradeExecutionEventService.appendEvent({
              tradeExecutionId: input.tradeExecutionId,
              tradeId: input.tradeId,
              sessionId: input.sessionId,
              operationId: autoCancelOpId,
              eventType: 'order-cancelled',
              occurredAt: input.occurredAt,
              targetOrderEventId: order.id,
            });
          }
        } else {
          // ── Step 3: partial-exit → validate per-type reservation ──────────────
          // Re-read execution to get the updated remainingQuantity (from step 1's projection).
          const updatedExecution = await this.table.get(input.tradeExecutionId);
          const newRemainingQty = updatedExecution?.remainingQuantity ?? 0;

          const freshEvents = await db.trade_execution_events
            .where('tradeExecutionId')
            .equals(input.tradeExecutionId)
            .filter(e => !isTombstoned(e))
            .toArray();

          const slReservation = computeActiveOppositeOrderReservation(freshEvents, positionDirection, 'stop');
          if (slReservation > newRemainingQty) {
            throw new Error(
              `Stop Loss reservation (${slReservation}) exceeds new remaining position quantity (${newRemainingQty}). Cancel or reduce active Stop Loss orders before recording this exit.`,
            );
          }
          const tpReservation = computeActiveOppositeOrderReservation(freshEvents, positionDirection, 'limit');
          if (tpReservation > newRemainingQty) {
            throw new Error(
              `Take Profit reservation (${tpReservation}) exceeds new remaining position quantity (${newRemainingQty}). Cancel or reduce active Take Profit orders before recording this exit.`,
            );
          }
        }

        // ── Step 4: final projection update (includes all cancel/amend events) ──
        await this.updateProjectionFromEvent(input.tradeExecutionId, event);
      }

      result = await this.table.get(input.tradeExecutionId);
    });

    if (!result) {
      throw new Error(
        `[TradeExecutionService] logClose: TradeExecution ${input.tradeExecutionId} not found after transaction`
      );
    }

    return result;
  }

  /**
   * Log an order-placed event on an existing TradeExecution.
   *
   * Non-financial event: no balance row required.
   * §8.4 atomic write: appendEvent → updateProjectionFromEvent (steps 1–2 only).
   * appendEvent() is idempotent by operationId — safe to retry.
   *
   * @returns The updated TradeExecution after projection recompute
   */
  async logOrder(input: LogOrderInput): Promise<TradeExecution> {
    let result: TradeExecution | undefined;

    await db.transaction('rw', [
      db.trade_execution_events,
      db.trade_executions,
      db.sync_operations,
      db.sync_state,
    ], async () => {
      markAsInstrumentedWriteContext();

      // T02 (WU2D): opposite-direction reservation cap guard.
      const currentExecution = await this.table.get(input.tradeExecutionId);
      if (!currentExecution) {
        throw new Error(
          `[TradeExecutionService] logOrder: TradeExecution ${input.tradeExecutionId} not found`
        );
      }
      if (
        currentExecution.positionDirection !== undefined &&
        input.direction !== currentExecution.positionDirection
      ) {
        const events = await db.trade_execution_events
          .where('tradeExecutionId')
          .equals(input.tradeExecutionId)
          .filter(e => !isTombstoned(e))
          .toArray();
        const cap = computeMaxOppositeOrderQty(
          events,
          currentExecution.positionDirection,
          currentExecution.remainingQuantity ?? 0,
          input.orderType,
        );
        if (input.quantity > cap) {
          throw new Error(
            `Cannot place opposite-direction order — quantity ${input.quantity} exceeds available cap ${cap}.`
          );
        }

        // T02 (WU2E): relational price guard — TP must be on the correct side of SL and vice versa.
        if (input.orderType === 'stop' || input.orderType === 'limit') {
          const relational = computeRelationalPriceConstraint(
            events,
            currentExecution.positionDirection,
            input.price,
            input.orderType,
          );
          if (!relational.valid) {
            throw new Error(relational.reason ?? 'Price violates relational SL/TP constraint.');
          }
        }
      }

      const event = await tradeExecutionEventService.appendEvent({
        tradeExecutionId: input.tradeExecutionId,
        tradeId: input.tradeId,
        sessionId: input.sessionId,
        operationId: input.operationId,
        eventType: 'order-placed',
        occurredAt: input.occurredAt,
        direction: input.direction,
        quantity: input.quantity,
        price: input.price,
        orderType: input.orderType,
      });

      await this.updateProjectionFromEvent(input.tradeExecutionId, event);

      // No balance row — order-placed is non-financial (§8.4 table)
      result = await this.table.get(input.tradeExecutionId);
    });

    if (!result) {
      throw new Error(
        `[TradeExecutionService] logOrder: TradeExecution ${input.tradeExecutionId} not found after transaction`
      );
    }

    return result;
  }

  /**
   * Log an order-amended event on an existing TradeExecution.
   *
   * Non-financial event: no balance row required.
   * §8.4 atomic write: appendEvent → updateProjectionFromEvent (steps 1–2 only).
   * appendEvent() is idempotent by operationId — safe to retry.
   *
   * At least one of `quantity` or `price` must be provided.
   *
   * @returns The updated TradeExecution after projection recompute
   */
  async logAmend(input: LogAmendInput): Promise<TradeExecution> {
    let result: TradeExecution | undefined;

    await db.transaction('rw', [
      db.trade_execution_events,
      db.trade_executions,
      db.sync_operations,
      db.sync_state,
    ], async () => {
      markAsInstrumentedWriteContext();

      // T02 (WU2D): opposite-direction amend cap guard.
      // Only applies when a new quantity is specified — price-only amendments are uncapped.
      if (input.quantity !== undefined) {
        const currentExecution = await this.table.get(input.tradeExecutionId);
        if (!currentExecution) {
          throw new Error(
            `[TradeExecutionService] logAmend: TradeExecution ${input.tradeExecutionId} not found`
          );
        }
        if (currentExecution.positionDirection !== undefined) {
          const events = await db.trade_execution_events
            .where('tradeExecutionId')
            .equals(input.tradeExecutionId)
            .filter(e => !isTombstoned(e))
            .toArray();

          const targetOrder = events.find(
            e => e.eventType === 'order-placed' && e.id === input.targetOrderEventId,
          );
          if (!targetOrder) {
            throw new Error(
              `Cannot amend — target order event ${input.targetOrderEventId} not found.`
            );
          }

          if (targetOrder.direction !== currentExecution.positionDirection) {
            // Resolve target order's current quantity — latest qty-carrying amendment wins.
            // Exclude price-only amendments so they don't revert to the original order qty.
            const orderAmends = events.filter(
              e => e.eventType === 'order-amended' && e.targetOrderEventId === targetOrder.id,
            );
            const qtyAmends = orderAmends.filter(e => e.quantity !== undefined);
            const latestQtyAmend =
              qtyAmends.length > 0
                ? qtyAmends.reduce((a, b) => {
                    if (a.occurredAt !== b.occurredAt) return a.occurredAt > b.occurredAt ? a : b;
                    return a.createdAt > b.createdAt ? a : b;
                  })
                : undefined;
            const targetCurrentQty = latestQtyAmend?.quantity ?? targetOrder.quantity;

            if (targetCurrentQty !== undefined) {
              const targetFilledQty = events
                .filter(
                  e =>
                    (e.eventType === 'entry-fill' ||
                      e.eventType === 'partial-exit' ||
                      e.eventType === 'final-exit') &&
                    e.targetOrderEventId === targetOrder.id,
                )
                .reduce((sum, e) => sum + (e.quantity ?? 0), 0);

              const targetRemainingQty = targetCurrentQty - targetFilledQty;
              const cap = computeMaxOppositeOrderQty(
                events,
                currentExecution.positionDirection,
                currentExecution.remainingQuantity ?? 0,
                targetOrder.orderType,
              );
              const amendCap = cap + targetRemainingQty;

              if (input.quantity > amendCap) {
                throw new Error(
                  `Cannot amend opposite-direction order — quantity ${input.quantity} exceeds amend cap ${amendCap}.`
                );
              }
            }

            // T02 (WU2E): relational price guard for quantity+price amends on typed exit orders.
            if (
              input.price !== undefined &&
              (targetOrder.orderType === 'stop' || targetOrder.orderType === 'limit')
            ) {
              const relational = computeRelationalPriceConstraint(
                events,
                currentExecution.positionDirection,
                input.price,
                targetOrder.orderType,
              );
              if (!relational.valid) {
                throw new Error(relational.reason ?? 'Price violates relational SL/TP constraint.');
              }
            }
          }
        }
      }

      // T02 (WU2E): relational price guard for price-only amends on typed exit orders.
      // The quantity guard block above is skipped when input.quantity === undefined, so
      // we handle the price-only case here with a separate event fetch.
      if (input.quantity === undefined && input.price !== undefined) {
        const currentExecution = await this.table.get(input.tradeExecutionId);
        if (currentExecution?.positionDirection !== undefined) {
          const events = await db.trade_execution_events
            .where('tradeExecutionId')
            .equals(input.tradeExecutionId)
            .filter(e => !isTombstoned(e))
            .toArray();
          const targetOrder = events.find(
            e => e.eventType === 'order-placed' && e.id === input.targetOrderEventId,
          );
          if (
            targetOrder !== undefined &&
            targetOrder.direction !== currentExecution.positionDirection &&
            (targetOrder.orderType === 'stop' || targetOrder.orderType === 'limit')
          ) {
            const relational = computeRelationalPriceConstraint(
              events,
              currentExecution.positionDirection,
              input.price,
              targetOrder.orderType,
            );
            if (!relational.valid) {
              throw new Error(relational.reason ?? 'Price violates relational SL/TP constraint.');
            }
          }
        }
      }

      const event = await tradeExecutionEventService.appendEvent({
        tradeExecutionId: input.tradeExecutionId,
        tradeId: input.tradeId,
        sessionId: input.sessionId,
        operationId: input.operationId,
        eventType: 'order-amended',
        occurredAt: input.occurredAt,
        targetOrderEventId: input.targetOrderEventId,
        ...(input.quantity !== undefined ? { quantity: input.quantity } : {}),
        ...(input.price !== undefined ? { price: input.price } : {}),
      });

      await this.updateProjectionFromEvent(input.tradeExecutionId, event);

      // No balance row — order-amended is non-financial (§8.4 table)
      result = await this.table.get(input.tradeExecutionId);
    });

    if (!result) {
      throw new Error(
        `[TradeExecutionService] logAmend: TradeExecution ${input.tradeExecutionId} not found after transaction`
      );
    }

    return result;
  }

  /**
   * Log an order-cancelled event on an existing TradeExecution.
   *
   * Non-financial event: no balance row required.
   * §8.4 atomic write: appendEvent → updateProjectionFromEvent (steps 1–2 only).
   * appendEvent() is idempotent by operationId — safe to retry.
   *
   * If cancelled before any entry-fills, projection recompute sets
   * executionStatus = 'cancelled'. OCO sibling cancels (position already open)
   * leave executionStatus unchanged.
   *
   * @returns The updated TradeExecution after projection recompute
   */
  async logCancelOrder(input: LogCancelOrderInput): Promise<TradeExecution> {
    let result: TradeExecution | undefined;

    await db.transaction('rw', [
      db.trade_execution_events,
      db.trade_executions,
      db.sync_operations,
      db.sync_state,
    ], async () => {
      markAsInstrumentedWriteContext();

      const event = await tradeExecutionEventService.appendEvent({
        tradeExecutionId: input.tradeExecutionId,
        tradeId: input.tradeId,
        sessionId: input.sessionId,
        operationId: input.operationId,
        eventType: 'order-cancelled',
        occurredAt: input.occurredAt,
        targetOrderEventId: input.targetOrderEventId,
      });

      await this.updateProjectionFromEvent(input.tradeExecutionId, event);

      // No balance row — order-cancelled is non-financial (§8.4 table)
      result = await this.table.get(input.tradeExecutionId);
    });

    if (!result) {
      throw new Error(
        `[TradeExecutionService] logCancelOrder: TradeExecution ${input.tradeExecutionId} not found after transaction`
      );
    }

    return result;
  }

  // ===== PRIVATE HELPERS =====

  /**
   * Derive TradeExecution projection fields by replaying all events for this execution.
   *
   * Reads all non-tombstoned events for `tradeExecutionId` from the DB (within the
   * active transaction), sorts by occurredAt, and computes:
   * - executionStatus (from event sequence)
   * - remainingQuantity (entries minus exits)
   * - avgEntryPrice (VWAP of entry-fill events)
   * - avgExitPrice (VWAP of partial-exit + final-exit events)
   * - openedAt (timestamp of first entry-fill)
   * - closedAt (timestamp of final-exit)
   * - closedSessionId (sessionId of final-exit event)
   * - stopLossCurrent (price of the single active stop exit order; undefined when 0 or 2+)
   * - takeProfitCurrent (price of the single active limit exit order; undefined when 0 or 2+)
   *
   * @param tradeExecutionId - Execution to recompute projection for
   * @returns Partial TradeExecution with all changed projection fields
   */
  private async recomputeProjection(tradeExecutionId: string): Promise<ProjectionChanges> {
    const events = await db.trade_execution_events
      .where('tradeExecutionId')
      .equals(tradeExecutionId)
      .filter(e => !isTombstoned(e))
      .toArray();

    events.sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));

    // Accumulators
    let executionStatus: TradeExecutionStatus = 'pending';
    let remainingQuantity = 0;
    let totalEntryQty = 0;
    let totalEntryValue = 0;
    let totalExitQty = 0;
    let totalExitValue = 0;
    let openedAt: string | undefined;
    let closedAt: string | undefined;
    let closedSessionId: string | undefined;
    // F-07 (PR62): direction of the position from the first entry-fill.
    // Persisted on the projection so callers can route opposite-direction fills correctly.
    let positionDirection: 'long' | 'short' | undefined;

    for (const event of events) {
      switch (event.eventType) {
        case 'entry-fill': {
          executionStatus = 'open';
          // F-01 (PR62) belt-and-suspenders: if an entry-fill follows a final-exit in the event
          // sequence, clear the close accumulators so the projection reflects the correct state.
          closedAt = undefined;
          closedSessionId = undefined;
          if (openedAt === undefined) {
            openedAt = event.occurredAt;
          }
          // F-07 (PR62): capture position direction from the first entry-fill.
          if (positionDirection === undefined && event.direction !== undefined) {
            positionDirection = event.direction;
          }
          const qty = event.quantity ?? 0;
          const price = event.price ?? 0;
          remainingQuantity += qty;
          totalEntryQty += qty;
          totalEntryValue += price * qty;
          break;
        }

        case 'partial-exit': {
          // Only advance to partial-exit if not already closed
          if (executionStatus !== 'closed') {
            executionStatus = 'partial-exit';
          }
          const qty = event.quantity ?? 0;
          const price = event.price ?? 0;
          remainingQuantity -= qty;
          totalExitQty += qty;
          totalExitValue += price * qty;
          break;
        }

        case 'final-exit': {
          executionStatus = 'closed';
          closedAt = event.occurredAt;
          closedSessionId = event.sessionId;
          const qty = event.quantity ?? 0;
          const price = event.price ?? 0;
          totalExitQty += qty;
          totalExitValue += price * qty;
          remainingQuantity = 0;
          break;
        }

        case 'order-cancelled': {
          // Cancelled before any fills — position never opened.
          // OCO sibling cancels and add-on order cancels (totalEntryQty > 0) are
          // internal order management; the core position remains open.
          if (totalEntryQty === 0) {
            executionStatus = 'cancelled';
          }
          break;
        }

        // order-placed, order-amended,
        // overnight-financing-applied, currency-conversion-applied:
        // no projection change — handled in BalanceHistory (step 3)
        default:
          break;
      }
    }

    // T02 (WU2E): Derive stopLossCurrent / takeProfitCurrent from active exit orders.
    // Decision 4: legacy stop-moved / take-profit-updated events are no longer the
    // behavioral source of truth. A single active stop exit order → stopLossCurrent.
    // A single active limit exit order → takeProfitCurrent.
    // Zero or multiple active orders of a type → field is left undefined (ambiguous).
    let stopLossCurrent: number | undefined;
    let takeProfitCurrent: number | undefined;
    if (positionDirection !== undefined) {
      const slPrices = getActiveOppositeOrderPrices(events, positionDirection, 'stop');
      stopLossCurrent = slPrices.length === 1 ? slPrices[0] : undefined;

      const tpPrices = getActiveOppositeOrderPrices(events, positionDirection, 'limit');
      takeProfitCurrent = tpPrices.length === 1 ? tpPrices[0] : undefined;
    }

    // Build changes object — only include fields that have a computed value
    const changes: ProjectionChanges = { executionStatus };

    if (totalEntryQty > 0) {
      changes.avgEntryPrice = totalEntryValue / totalEntryQty;
      changes.remainingQuantity = remainingQuantity;
    }
    if (totalExitQty > 0) {
      changes.avgExitPrice = totalExitValue / totalExitQty;
    }
    if (openedAt !== undefined) {
      changes.openedAt = openedAt;
    }
    if (closedAt !== undefined) {
      changes.closedAt = closedAt;
    }
    if (closedSessionId !== undefined) {
      changes.closedSessionId = closedSessionId;
    }
    if (stopLossCurrent !== undefined) {
      changes.stopLossCurrent = stopLossCurrent;
    }
    if (takeProfitCurrent !== undefined) {
      changes.takeProfitCurrent = takeProfitCurrent;
    }
    if (positionDirection !== undefined) {
      changes.positionDirection = positionDirection;
    }

    return changes;
  }
}

// Export singleton instance
export const tradeExecutionService = new TradeExecutionService();
